import { FC, useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Badge } from '@components';
import { borderRadius } from '@config/themes/common/borderRadius';
import { useDirection } from '@hooks';
import { SingularKeys, Trans, useTrans } from '@libs/i18n';
import { PencilSimpleLineIcon } from '@phosphor-icons/react';
import { NoFlipBox } from '@utils';

export interface AssessmentWorkflowHeaderProps {
  onExit?: () => void;
  assessmentName?: string;
  /**
   * Callback to persist a new assessment name
   * NOTE: API calls are handled in the page / hooks layer, not in this component.
   */
  onSaveName?: (newName: string) => Promise<void> | void;
  /** Optional loading state while saving the name */
  isSavingName?: boolean;
  assessmentLanguage?: {
    code: string;
    label: string;
  };
}


export const AssessmentWorkflowHeader: FC<AssessmentWorkflowHeaderProps> = observer(
  ({ onExit, assessmentName, onSaveName, isSavingName = false, assessmentLanguage }) => {
    const { t } = useTrans();
    const { dir, isRtl } = useDirection();

    const displayName = assessmentName || t('app.assessments.workflow.nameDefault');

    const [isEditing, setIsEditing] = useState(false);
    const [localName, setLocalName] = useState(displayName);
    const [error, setError] = useState<string>('');

    // Sync local state when assessment name changes from outside (e.g. API update)
    useEffect(() => {
      if (!isEditing) {
        setLocalName(displayName);
        setError('');
      }
    }, [displayName, isEditing]);

    const validateName = (value: string): string => {
      const trimmed = value.trim();
      if (!trimmed) {
        return t('app.assessments.workflow.nameRequired');
      }
      // Keep simple for now; maximum length handled on API side if needed
      return '';
    };

    const handleStartEditing = () => {
      if (isSavingName) return;
      setIsEditing(true);
      setError('');
    };

    const handleCancelEditing = () => {
      if (isSavingName) return;
      setIsEditing(false);
      setLocalName(displayName);
      setError('');
    };

    const handleConfirmEditing = async () => {
      if (!onSaveName || isSavingName) {
        setIsEditing(false);
        setLocalName(displayName);
        setError('');
        return;
      }

      const trimmed = localName.trim();
      const validationError = validateName(trimmed);
      if (validationError) {
        setError(validationError);
        return;
      }

      // Avoid unnecessary API calls if name didn't change
      if (trimmed === displayName.trim()) {
        setIsEditing(false);
        setError('');
        return;
      }

      try {
        await Promise.resolve(onSaveName(trimmed));
        setIsEditing(false);
        setError('');
      } catch {
        // Error feedback is handled by caller (page/hooks) via snackbar
        // Keep editing state so user can adjust the name
      }
    };

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header Row - Exit and Assessment Name */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* Exit Assessment Button - Left */}
          <Button
            variant="text"
            startIcon={<ArrowBackIcon sx={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />}
            onClick={onExit}
            sx={{
              minWidth: 'auto',
              textTransform: 'none',
              color: 'text.primary',
              backgroundColor: 'transparent',
              fontWeight: 400,
              fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
              '& .MuiButton-startIcon': {
                marginInlineEnd: { xs: 0.5, sm: 1 },
                '& > *:nth-of-type(1)': {
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                },
              },
              padding: 0,
              margin: 0,
            }}
          >
            <Typography
              variant="textMD_medium"
              sx={{
                color: 'text.primary',
                fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
              }}
            >
              <Trans
                i18nKey={'assessments.workflow.header.exitAssessment'}
              />
            </Typography>
          </Button>

            {/* Assessment Name / Inline Edit + Language Badge */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              {!isEditing ? (
                <Box
                  onClick={handleStartEditing}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: { xs: '100%', sm: '240px', md: '270px' },
                    height: { xs: '26px', sm: '27px', md: '28px' },
                    gap: { xs: 0.75, sm: 1 },
                    px: { xs: 1.5, sm: 1.75, md: 2 },
                    py: { xs: 0.375, sm: 0.5 },
                    borderRadius: borderRadius.sm,
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'grey.400',
                    cursor: 'pointer',
                    maxWidth: { xs: '100%', sm: 350, md: 420 },
                  }}
                >
                  <Typography
                    variant="textSM_medium"
                    sx={{
                      color: 'text.primary',
                      fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                    }}
                  >
                    {displayName}
                  </Typography>

                  <PencilSimpleLineIcon size={16} weight="regular" />
                </Box>
              ) : (
                <NoFlipBox
                  mode={dir}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 999,
                    backgroundColor: 'grey.100',
                    maxWidth: 520,
                  }}
                >
                  <TextField
                    value={localName}
                    onChange={(e) => {
                      setLocalName(e.target.value);
                      if (error) {
                        setError('');
                      }
                    }}
                    fullWidth
                    autoFocus
                    placeholder={displayName}
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: '0.875rem',
                        backgroundColor: 'grey.300',
                        borderRadius: 4,
                        p: 0.5,
                        paddingInlineStart: 3,
                      },
                    }}
                  />
                  <IconButton
                    onClick={handleCancelEditing}
                    size="small"
                    disabled={isSavingName}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={handleConfirmEditing}
                    size="small"
                    disabled={isSavingName}
                    sx={{
                      backgroundColor: 'common.black',
                      color: 'common.white',
                      '&:hover': {
                        backgroundColor: 'grey.900',
                      },
                      borderRadius: 2,
                    }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </NoFlipBox>
              )}
              {assessmentLanguage && (
                <Badge
                  variant="Language"
                  label={assessmentLanguage.label}
                  sx={{
                    height: '24px',
                    textTransform: 'none',
                    backgroundColor: '#F2F2F2',
                    color: 'grey.700',
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    px: 1,
                  }}
                />
              )}
            </Box>
        </Box>

        {/* Description Text */}
        <Typography
          variant="textXS_medium"
          sx={{
            color: 'black.200',
            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
            mt: 2,
          }}
        >
          <Trans
            i18nKey={'assessments.new.description' as SingularKeys}
            defaults="Set up your PDPL compliance assessment in one simple step"
          />
        </Typography>
      </Box>
    );
  },
);
